import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';

import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '../lib';
import { Spinner } from './spinner';

const buttonVariants = cva(
  'inline-flex items-center transition-color bg-surface-00 ease-in-out duration-200 justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-center hover:cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'bg-brand-500 text-base-500 hover:bg-electric-blue active:bg-active-brand-500-active disabled:bg-navy disabled:text-on-brand-500-040',
        primary:
          'bg-brand-500 text-base-500 hover:bg-electric-blue active:bg-active-brand-500-active disabled:bg-navy disabled:text-on-brand-500-040',
        destructive:
          'bg-negative-500 text-negative-500-foreground hover:bg-negative-500/90',
        'destructive-inverted': 'text-negative-500 bg-surface-00-transparent',
        'destructive-inverted-clean':
          'text-negative-500 bg-surface-00-transparent !p-0',
        outline:
          'border border-border bg-surface-00 hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-contrast-300 text-base-500 hover:bg-contrast-300/80',
        'secondary-foreground': 'bg-base-500 text-base-500 hover:bg-base-300',
        ghost:
          'hover:bg-accent hover:text-accent-foreground bg-surface-00-transparent',
        link: 'text-primary underline-offset-4 hover:underline hover:text-primary-hover',
        'modal-action':
          'bg-contrast-500-015 text-base-500 text-button-xs hover:bg-contrast-500-020 disabled:bg-contrast-500-005 disabled:text-icy-white',
        icon: 'bg-surface-00-transparent hover:bg-surface-00-transparent',
        clean: '!p-0 bg-surface-00-transparent',
        'clean-transparent': '!p-0 hover:bg-surface-00-transparent',
        dark: 'bg-neutral-900 text-base-500 active:bg-eerie-black hover:bg-dark-button-hover',
        lead: 'bg-base-300 hover:bg-eerie-black',
        'surface-01':
          'bg-surface-01 hover:bg-hover-surface-01-hover text-base-500',
        'vivid-red': 'bg-negative-500 text-base-500',
        'dark-gray': 'bg-dark-gray hover:bg-base-300 text-[17px] text-base-500',
        'blue-link':
          'text-primary text-sm font-medium hover:text-primary-hover active:text-primary-active transition-color ease-in-out duration-200 bg-surface-00-transparent',
        'dark-secondary':
          'bg-base-300 hover:bg-contrast-500-005 text-[17px] text-base-500',
        'brand-300':
          'bg-brand-300 hover:bg-hover-brand-300-hover text-brand-500',
        'base-300':
          'bg-base-300 hover:bg-hover-base-300-hover text-base leading-5 text-base-500',
        'base-500':
          'bg-base-500 hover:bg-hover-base-500-hover text-base leading-5 text-base-500',
        'brand-500':
          'bg-brand-500 hover:bg-hover-brand-500-hover text-base leading-5 text-base-500',
        'base-100':
          'bg-base-100 hover:bg-hover-base-100-hover text-base leading-5 text-base-500',
        'contrast-500':
          'bg-contrast-500 hover:bg-hover-contrast-500-hover text-base leading-5 text-contrast-500 disabled:bg-contrast-300',
        'd-secondary':
          'bg-brand-300 text-brand-500 hover:bg-hover-brand-300-hover active:bg-active-brand-300-active',
      },
      size: {
        default: 'px-[18px] py-[14px] text-[17px] leading-[22px]',
        sm: 'h-9 rounded-md px-3',
        md: 'h-10 rounded-xl px-3 py-1.5',
        lg: 'px-6 py-[14px] gap-1 text-[16px] leading-5 rounded-[50px] h-[64px]',
        icon: 'h-10 w-10',
        '2xl': 'py-3 px-6 min-h-[58px]',
        '13-r': 'text-[13px] leading-[18px] font-normal p-0 rounded-none ',
        '16-r': 'text-[16px] font-normal p-0 rounded-none ',
        rounded: 'rounded-full py-[14px] px-6 flex items-center justify-center',
        'rounded-2xl':
          'rounded-full size-20 p-0 flex items-center justify-center',
        'rounded-xl':
          'rounded-full size-14 p-0 flex items-center justify-center',
        'rounded-md':
          'rounded-full size-8 p-0 flex items-center justify-center',
        'rounded-sm':
          'rounded-full size-6 p-0 flex items-center justify-center',
        s: 'px-3 py-[14px] text-[14px] leading-5 rounded-xl h-10',

        'd-l':
          'px-6 py-[14px] min-h-[64px] text-base leading-5 font-medium rounded-full',
        'd-m':
          'px-4 py-[14px] min-h-12 text-base leading-5 font-medium rounded-2xl',
        'd-sm':
          'px-3 py-[14px] min-h-10 max-h-10 text-base leading-5 font-medium rounded-xl',
        'd-s': 'px-[14px] py-3 min-h-10 text-sm font-medium rounded-xl',
        resizes:
          'px-6 py-[22px] gap-1 typography-16m rounded-[50px] h-[64px] lg:py-[14px] lg:rounded-[12px] lg:h-[50px] lg:text-[17px] lg:leading-[22px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), {
          relative: loading,
        })}
        ref={ref}
        {...props}
        disabled={loading || disabled}
      >
        {loading && (
          <Spinner
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
            variant={variant === 'contrast-500' ? 'surface-00' : 'contrast-500'}
            size="sm"
          />
        )}
        {loading ? <div className="invisible">{children}</div> : children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
